import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, FolderArchive, Plus, Search, X, Trash2, AlertTriangle, FileText, ExternalLink } from 'lucide-react';
import { atlasApi, AtlasUser, DocumentoColab, DocumentoTipo, DocumentoStatus } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const TIPOS: { id: DocumentoTipo; label: string }[] = [
  { id: 'aso', label: 'ASO' },
  { id: 'nr06', label: 'NR-06 (EPI)' },
  { id: 'nr10', label: 'NR-10' },
  { id: 'nr35', label: 'NR-35' },
  { id: 'exame_periodico', label: 'Exame periódico' },
  { id: 'contrato', label: 'Contrato' },
  { id: 'rg', label: 'RG' },
  { id: 'cpf', label: 'CPF' },
  { id: 'ctps', label: 'CTPS' },
  { id: 'titulo_eleitor', label: 'Título de eleitor' },
  { id: 'reservista', label: 'Reservista' },
  { id: 'comprovante_residencia', label: 'Comprovante de residência' },
  { id: 'diploma', label: 'Diploma' },
  { id: 'certificado', label: 'Certificado' },
  { id: 'outro', label: 'Outro' },
];

const tipoLabel = (t: string) => TIPOS.find(x => x.id === t)?.label || t;

const STATUS_META: Record<DocumentoStatus, { label: string; cor: string }> = {
  valido: { label: 'Válido', cor: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  vence_30d: { label: 'Vence em 30d', cor: 'bg-amber-100 text-amber-700 border-amber-200' },
  vencido: { label: 'Vencido', cor: 'bg-rose-100 text-rose-700 border-rose-200' },
  sem_validade: { label: 'Sem validade', cor: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const fmtData = (s?: string | null) => {
  if (!s) return '—';
  const [a, m, d] = s.split('-');
  return d ? `${d}/${m}/${a}` : s;
};

interface FormState {
  tipo: DocumentoTipo;
  titulo: string;
  arquivo_url: string;
  data_emissao: string;
  data_validade: string;
  observacao: string;
}

const vazio = (): FormState => ({ tipo: 'aso', titulo: '', arquivo_url: '', data_emissao: '', data_validade: '', observacao: '' });

interface ColabSel {
  funccodi: number;
  nome?: string;
  cargo?: string;
  tem_foto?: boolean;
  data_sinc?: string;
}

const DocumentosApp: React.FC<Props> = ({ user, podeEditar }) => {
  const [vencimentos, setVencimentos] = useState<DocumentoColab[]>([]);
  const [loadingVenc, setLoadingVenc] = useState(true);

  const [colab, setColab] = useState<ColabSel | null>(null);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);

  const [docs, setDocs] = useState<DocumentoColab[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);

  const carregarVenc = useCallback(() => {
    setLoadingVenc(true);
    atlasApi.getDocumentosVencimentos(user, 30)
      .then(r => setVencimentos(r.itens || []))
      .catch(() => setVencimentos([]))
      .finally(() => setLoadingVenc(false));
  }, [user]);

  useEffect(() => { carregarVenc(); }, [carregarVenc]);

  useEffect(() => {
    if (colab) return;
    const termo = busca.trim();
    if (termo.length < 2) { setResultados([]); return; }
    let cancel = false;
    setBuscando(true);
    const t = window.setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: termo, status: 'ativo', per_page: 8 })
        .then((r: any) => { if (!cancel) setResultados(r?.itens || []); })
        .catch(() => { if (!cancel) setResultados([]); })
        .finally(() => { if (!cancel) setBuscando(false); });
    }, 300);
    return () => { cancel = true; window.clearTimeout(t); };
  }, [user, colab, busca]);

  const carregarDocs = useCallback((funccodi: number) => {
    setLoadingDocs(true);
    atlasApi.getDocumentosColaborador(user, funccodi)
      .then(r => setDocs(r.itens || []))
      .catch(e => setFb({ tipo: 'erro', msg: e.message || 'Erro ao carregar' }))
      .finally(() => setLoadingDocs(false));
  }, [user]);

  const selecionar = (c: any) => {
    const sel: ColabSel = {
      funccodi: c.funccodi,
      nome: c.dados_publicos?.nome,
      cargo: c.dados_publicos?.cargo_nome,
      tem_foto: c.dados_publicos?.tem_foto_gix,
      data_sinc: c.data_sincronizacao,
    };
    setColab(sel);
    setBusca('');
    setResultados([]);
    carregarDocs(sel.funccodi);
  };

  const salvar = async () => {
    if (!colab || !form.titulo.trim()) return;
    setSalvando(true);
    setFb(null);
    try {
      await atlasApi.criarDocumento(user, colab.funccodi, {
        tipo: form.tipo,
        titulo: form.titulo.trim(),
        arquivo_url: form.arquivo_url.trim() || undefined,
        data_emissao: form.data_emissao || undefined,
        data_validade: form.data_validade || undefined,
        observacao: form.observacao.trim() || undefined,
      });
      setModal(false);
      setForm(vazio());
      carregarDocs(colab.funccodi);
      carregarVenc();
      setFb({ tipo: 'ok', msg: 'Documento adicionado' });
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (d: DocumentoColab) => {
    if (!colab) return;
    if (!window.confirm(`Remover o documento "${d.titulo}"?`)) return;
    try {
      await atlasApi.excluirDocumento(user, d._id);
      carregarDocs(colab.funccodi);
      carregarVenc();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao remover' });
    }
  };

  return (
    <div className="space-y-5">
      {/* Vencimentos */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 className="font-semibold text-gray-900 text-sm">Vencimentos próximos (30 dias)</h3>
          {!loadingVenc && <span className="text-xs text-gray-400">· {vencimentos.length}</span>}
        </div>
        {loadingVenc ? (
          <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : vencimentos.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">Nenhum documento vencendo. Tudo em dia. ✅</div>
        ) : (
          <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
            {vencimentos.map(d => {
              const sm = STATUS_META[d.status];
              return (
                <div key={d._id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                  <FileText className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{d.titulo} <span className="text-gray-400 font-normal">· {tipoLabel(d.tipo)}</span></div>
                    <div className="text-xs text-gray-400 truncate">{d.colaborador_nome || `Mat. ${d.funccodi}`}</div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${sm.cor}`}>{sm.label}</span>
                    <div className="text-xs text-gray-400 mt-0.5">{fmtData(d.data_validade)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Seleção de colaborador */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FolderArchive className="w-5 h-5 text-purple-600" /> Documentos por colaborador
          </h3>
          {colab && podeEditar && (
            <button
              onClick={() => { setForm(vazio()); setModal(true); setFb(null); }}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-xl text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Novo documento
            </button>
          )}
        </div>

        {colab ? (
          <div className="mt-4">
            <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2 mb-4">
              <AvatarColaborador nome={colab.nome} fotoUrl={colab.tem_foto ? atlasApi.fotoUrl(user, colab.funccodi, colab.data_sinc) : undefined} size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{colab.nome}</div>
                <div className="text-xs text-gray-400 truncate">{colab.cargo || '—'}</div>
              </div>
              <button onClick={() => { setColab(null); setDocs([]); }} className="text-gray-400 hover:text-gray-600 text-sm">trocar</button>
            </div>

            {fb && <div className="mb-3"><FeedbackBanner feedback={fb} /></div>}

            {loadingDocs ? (
              <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : docs.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">Nenhum documento cadastrado para este colaborador.</div>
            ) : (
              <div className="border border-gray-100 rounded-xl divide-y divide-gray-50 overflow-hidden">
                {docs.map(d => {
                  const sm = STATUS_META[d.status];
                  return (
                    <div key={d._id} className="flex items-center gap-3 px-4 py-3">
                      <FileText className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                          {d.titulo}
                          {d.arquivo_url && (
                            <a href={d.arquivo_url} target="_blank" rel="noreferrer" className="text-purple-500 hover:text-purple-700">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {tipoLabel(d.tipo)}
                          {d.data_validade && <> · validade {fmtData(d.data_validade)}</>}
                        </div>
                      </div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${sm.cor}`}>{sm.label}</span>
                      {podeEditar && (
                        <button onClick={() => excluir(d)} className="text-gray-300 hover:text-rose-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 relative max-w-md">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar colaborador..."
              className={`${inputCls} pl-9`}
            />
            {buscando && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />}
            {resultados.length > 0 && (
              <div className="mt-2 border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-60 overflow-y-auto">
                {resultados.map((c: any) => (
                  <button key={c._id || c.funccodi} onClick={() => selecionar(c)} className="flex items-center gap-3 w-full px-3 py-2 hover:bg-purple-50/50 text-left">
                    <AvatarColaborador nome={c.dados_publicos?.nome} fotoUrl={c.dados_publicos?.tem_foto_gix ? atlasApi.fotoUrl(user, c.funccodi, c.data_sincronizacao) : undefined} size={32} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{c.dados_publicos?.nome}</div>
                      <div className="text-xs text-gray-400 truncate">{c.dados_publicos?.cargo_nome || '—'}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Novo documento</h3>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {fb && <FeedbackBanner feedback={fb} />}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as DocumentoTipo }))} className={inputCls}>
                    {TIPOS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Título</label>
                  <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} className={inputCls} placeholder="Ex.: ASO admissional" />
                </div>
                <div>
                  <label className={labelCls}>Emissão</label>
                  <input type="date" value={form.data_emissao} onChange={e => setForm(f => ({ ...f, data_emissao: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Validade</label>
                  <input type="date" value={form.data_validade} onChange={e => setForm(f => ({ ...f, data_validade: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Link do arquivo (opcional)</label>
                <input value={form.arquivo_url} onChange={e => setForm(f => ({ ...f, arquivo_url: e.target.value }))} className={inputCls} placeholder="https://..." />
              </div>
              <div>
                <label className={labelCls}>Observação</label>
                <textarea value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
              <button onClick={salvar} disabled={!form.titulo.trim() || salvando} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-sm font-medium">
                {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosApp;
